import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../../components/ui/input-otp";
import { toast } from "sonner";
import {
  LayoutDashboard,
  FileText,
  Calendar,
  Vote,
  FileSpreadsheet,
  CheckSquare,
  Plus,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsentModule = () => {
  const { consortiumId } = useParams();
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [selectedConsent, setSelectedConsent] = useState(null);
  const [otp, setOtp] = useState("");
  const [consentType, setConsentType] = useState("join_consortium");
  const [termsAccepted, setTermsAccepted] = useState(false);

  const sidebarItems = [
    {
      section: "Consortium",
      items: [
        { name: "Overview", path: `/consortium/${consortiumId}`, icon: LayoutDashboard },
        { name: "Agreements", path: `/consortium/${consortiumId}/agreements`, icon: FileText },
        { name: "Meetings", path: `/consortium/${consortiumId}/meetings`, icon: Calendar },
        { name: "Voting", path: `/consortium/${consortiumId}/voting`, icon: Vote },
        { name: "RFPs & Bids", path: `/consortium/${consortiumId}/rfps`, icon: FileSpreadsheet },
        { name: "Consent", path: `/consortium/${consortiumId}/consent`, icon: CheckSquare },
      ],
    },
  ];

  useEffect(() => {
    fetchConsents();
  }, [consortiumId]);

  const fetchConsents = async () => {
    try {
      const response = await axios.get(`${API}/consents?consortium_id=${consortiumId}`);
      setConsents(response.data);
    } catch (error) {
      console.error("Failed to fetch consents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConsent = async () => {
    if (!termsAccepted) {
      toast.error("Please accept the terms to continue");
      return;
    }

    try {
      const response = await axios.post(`${API}/consents`, {
        consortium_id: consortiumId,
        consent_type: consentType,
        terms_accepted: termsAccepted,
      });
      
      toast.success("Consent created! OTP sent for verification.");
      setDialogOpen(false);
      setSelectedConsent(response.data);
      setOtpDialogOpen(true);
      
      // Show mock OTP in dev
      toast.info(`Mock OTP: ${response.data.mock_otp}`, { duration: 10000 });
      
      fetchConsents();
    } catch (error) {
      toast.error("Failed to create consent");
    }
  };

  const handleVerifyOTP = async () => {
    if (!selectedConsent) return;

    try {
      await axios.post(`${API}/consents/${selectedConsent.consent_id}/verify`, null, {
        params: { otp },
      });
      toast.success("Consent verified successfully!");
      setOtpDialogOpen(false);
      setOtp("");
      setSelectedConsent(null);
      fetchConsents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP");
    }
  };

  const handleWithdraw = async (consentId) => {
    try {
      await axios.post(`${API}/consents/${consentId}/withdraw`);
      toast.success("Consent withdrawn");
      fetchConsents();
    } catch (error) {
      toast.error("Failed to withdraw consent");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "verified":
        return (
          <Badge className="bg-[#4E80FF]">
            <CheckCircle className="w-3 h-3 mr-1" />
            Verified
          </Badge>
        );
      case "pending":
        return (
          <Badge variant="secondary">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case "withdrawn":
        return (
          <Badge variant="outline" className="text-red-600">
            <XCircle className="w-3 h-3 mr-1" />
            Withdrawn
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const consentTypes = {
    join_consortium: {
      title: "Join Consortium",
      description: "Consent to become a member of this consortium and participate in land pooling.",
    },
    agreement_terms: {
      title: "Agreement Terms",
      description: "Consent to the terms and conditions of the consortium agreement.",
    },
    data_sharing: {
      title: "Data Sharing",
      description: "Consent to share your land and personal data with consortium members and authorities.",
    },
  };

  return (
    <DashboardLayout sidebarItems={sidebarItems}>
      <div className="space-y-6" data-testid="consent-module">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              Consent Management
            </h1>
            <p className="text-slate-600">
              Manage your consents with OTP verification
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="give-consent-btn">
                <Plus className="w-4 h-4 mr-2" />
                Give Consent
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Provide Consent</DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                <div className="space-y-4">
                  <Label>Select Consent Type</Label>
                  {Object.entries(consentTypes).map(([key, value]) => (
                    <div
                      key={key}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        consentType === key
                          ? "border-[#4E80FF] bg-blue-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      onClick={() => setConsentType(key)}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-4 h-4 rounded-full border-2 ${
                            consentType === key
                              ? "border-[#4E80FF] bg-[#4E80FF]"
                              : "border-slate-300"
                          }`}
                        >
                          {consentType === key && (
                            <div className="w-full h-full flex items-center justify-center">
                              <CheckCircle className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{value.title}</p>
                          <p className="text-sm text-slate-500">{value.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-900 mb-2">Terms & Conditions</h4>
                  <div className="text-sm text-slate-600 space-y-2 max-h-32 overflow-y-auto">
                    <p>By providing consent, you agree to:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Share relevant land documents with the consortium</li>
                      <li>Participate in consortium decisions through voting</li>
                      <li>Abide by the consortium agreement terms</li>
                      <li>Allow data processing as per privacy policy</li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(checked)}
                  />
                  <Label htmlFor="terms" className="text-sm leading-tight cursor-pointer">
                    I have read and agree to the terms and conditions. I understand that
                    this consent can be withdrawn at any time.
                  </Label>
                </div>

                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    className="btn-primary"
                    onClick={handleCreateConsent}
                    disabled={!termsAccepted}
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Submit & Get OTP
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* OTP Verification Dialog */}
        <Dialog open={otpDialogOpen} onOpenChange={setOtpDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Verify OTP</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 text-center">
              <p className="text-slate-600">
                Enter the 6-character OTP sent to your registered email/phone.
              </p>
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={(value) => setOtp(value)}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                className="w-full btn-primary"
                onClick={handleVerifyOTP}
                disabled={otp.length !== 6}
                data-testid="verify-otp-btn"
              >
                Verify Consent
              </Button>
              <p className="text-xs text-slate-500">
                Didn't receive OTP?{" "}
                <button className="text-[#4E80FF] hover:underline">Resend</button>
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent" />
          </div>
        ) : consents.length > 0 ? (
          <div className="space-y-4">
            {consents.map((consent) => (
              <Card key={consent.id} className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          consent.status === "verified"
                            ? "bg-blue-100"
                            : consent.status === "withdrawn"
                            ? "bg-red-100"
                            : "bg-yellow-100"
                        }`}
                      >
                        {consent.status === "verified" ? (
                          <CheckCircle className="w-6 h-6 text-[#4E80FF]" />
                        ) : consent.status === "withdrawn" ? (
                          <XCircle className="w-6 h-6 text-red-500" />
                        ) : (
                          <Clock className="w-6 h-6 text-yellow-500" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-slate-900 capitalize">
                          {consent.consent_type?.replace("_", " ")}
                        </h3>
                        <p className="text-sm text-slate-500">
                          Created: {new Date(consent.created_at).toLocaleDateString()}
                          {consent.verification_timestamp && (
                            <> • Verified: {new Date(consent.verification_timestamp).toLocaleDateString()}</>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(consent.status)}
                      {consent.status === "verified" && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleWithdraw(consent.id)}
                        >
                          <AlertTriangle className="w-4 h-4 mr-1" />
                          Withdraw
                        </Button>
                      )}
                      {consent.status === "pending" && (
                        <Button
                          size="sm"
                          className="btn-primary"
                          onClick={() => {
                            setSelectedConsent({ consent_id: consent.id });
                            setOtpDialogOpen(true);
                          }}
                        >
                          Verify
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-16 text-center">
              <Shield className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                No Consents Recorded
              </h3>
              <p className="text-slate-500 mb-6">
                Provide your consent to participate in consortium activities.
              </p>
              <Button className="btn-primary" onClick={() => setDialogOpen(true)}>
                Give Consent
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex gap-4">
              <Shield className="w-8 h-8 text-[#4E80FF] flex-shrink-0" />
              <div>
                <h4 className="font-heading font-semibold text-slate-900 mb-2">
                  About Consent Management
                </h4>
                <p className="text-sm text-slate-600">
                  All consents are verified through OTP for legal compliance. Your consent
                  records are immutable and stored securely. You can withdraw consent at
                  any time, subject to consortium rules. Withdrawal may affect your
                  membership status and rights within the consortium.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ConsentModule;
